import {
    ReactEventHandler,
    SyntheticEvent,
    useCallback,
    useState,
} from 'react';
import {
    MutationMode,
    RedirectionSideEffect,
    useNotify,
    useRedirect,
    useRefresh,
} from 'react-admin';
import { UseMutationOptions } from 'react-query';

import { useDeleteBranch } from '../dataProvider';
import { DeleteBranchParams, TreeRecord } from '../types';

/**
 * Prepare a set of callbacks for a delete branch button guarded by confirmation dialog
 *
 * @example
 *
 * const DeleteBranchButton = ({
 *     resource,
 *     record,
 *     redirect,
 *     onClick,
 *     ...rest
 * }) => {
 *     const {
 *         open,
 *         isLoading,
 *         handleDialogOpen,
 *         handleDialogClose,
 *         handleDelete,
 *     } = useDeleteBranchWithConfirmController({
 *         resource,
 *         record,
 *         redirect,
 *         onClick,
 *     });
 *
 *     return (
 *         <Fragment>
 *             <Button
 *                 onClick={handleDialogOpen}
 *                 label="ra.action.delete"
 *                 {...rest}
 *             >
 *                 {icon}
 *             </Button>
 *             <Confirm
 *                 isOpen={open}
 *                 loading={isLoading}
 *                 title="ra.message.delete_title"
 *                 content="ra.message.delete_content"
 *                 translateOptions={{
 *                     name: resource,
 *                     id: record.id,
 *                 }}
 *                 onConfirm={handleDelete}
 *                 onClose={handleDialogClose}
 *             />
 *         </Fragment>
 *     );
 * };
 */
export const useDeleteBranchWithConfirmController = <
    RecordType extends TreeRecord = TreeRecord
>({
    mutationMode,
    mutationOptions,
    record,
    redirect: redirectTo,
    resource,
}: UseDeleteBranchWithConfirmControllerParams<RecordType>): UseDeleteBranchWithConfirmControllerReturn => {
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const [deleteBranch, { isLoading }] = useDeleteBranch<RecordType>();
    const [open, setOpen] = useState(false);

    const handleDelete = useCallback(
        event => {
            event.stopPropagation();

            deleteBranch(
                resource,
                {
                    id: record.id,
                    previousData: record,
                },
                {
                    mutationMode,
                    onSuccess: () => {
                        notify('ra.notification.deleted', {
                            type: 'info',
                            messageArgs: { smart_count: 1 },
                        });
                        redirect(redirectTo, resource);
                        refresh();
                    },
                    onError: error => {
                        notify(
                            typeof error === 'string'
                                ? error
                                : error.message || 'ra.notification.http_error',
                            { type: 'warning' }
                        );
                    },
                    ...mutationOptions,
                }
            );
        },
        [
            deleteBranch,
            mutationMode,
            mutationOptions,
            notify,
            record,
            redirect,
            redirectTo,
            refresh,
            resource,
        ]
    );

    const handleDialogOpen = e => {
        e.stopPropagation();
        setOpen(true);
    };

    const handleDialogClose = e => {
        e.stopPropagation();
        setOpen(false);
    };

    return {
        handleDelete,
        handleDialogOpen,
        handleDialogClose,
        isLoading,
        open,
    };
};

export interface UseDeleteBranchWithConfirmControllerParams<
    RecordType extends TreeRecord = TreeRecord
> {
    mutationMode?: MutationMode;
    mutationOptions?: UseMutationOptions<
        RecordType,
        Error,
        DeleteBranchParams<RecordType>
    >;
    record?: RecordType;
    redirect?: RedirectionSideEffect;
    resource: string;
}

export interface UseDeleteBranchWithConfirmControllerReturn {
    isLoading: boolean;
    handleDelete: ReactEventHandler<any>;
    handleDialogOpen: (e: SyntheticEvent) => void;
    handleDialogClose: (e: SyntheticEvent) => void;
    open: boolean;
}
